/**
 * Language registry — ported from ThemeMaker's
 * {@code lms-portal/src/lib/platform/languages.ts}. Single source of
 * truth for locale metadata used across admin and portal:
 *
 * <ul>
 *   <li>Admin: drives the locale picker so users don't free-type
 *       two-letter codes and end up with junk like "TF" or "BT".</li>
 *   <li>Portal: {@code LanguageSwitcher}'s native-name table reads
 *       from here so the dropdown shows "Türkçe" / "中文" / "العربية"
 *       instead of uppercased two-letter fallbacks.</li>
 * </ul>
 *
 * <p>Each entry carries:
 * <ul>
 *   <li>{@code code} — BCP-47-ish locale code (lowercase, no region by
 *       default; "en-GB" / "pt-BR" / "zh-Hans" pass through unmodified
 *       for tenants that need region splits).</li>
 *   <li>{@code name} — English exonym for admin picker labels
 *       ("Spanish" not "Español").</li>
 *   <li>{@code nativeName} — endonym for the portal switcher
 *       ("Español" not "Spanish").</li>
 *   <li>{@code flag} — emoji flag or fallback globe glyph for
 *       multi-region languages.</li>
 * </ul>
 *
 * <p>Conscious decisions:
 * <ul>
 *   <li>Single code per language by default. Tenants needing
 *       {@code pt-BR}, {@code zh-CN}, {@code zh-TW}, {@code en-GB}
 *       etc. author the code manually and the registry quietly
 *       accepts the unfamiliar one (display falls back to the entry's
 *       base or to the code itself).</li>
 *   <li>Norwegian uses {@code 'no'} (not {@code 'nb'} / {@code 'nn'}).</li>
 *   <li>Hebrew uses modern {@code 'he'} (not legacy {@code 'iw'}).</li>
 *   <li>Indonesian uses modern {@code 'id'} (not legacy {@code 'in'}).</li>
 *   <li>"Filipino (Tagalog)" rather than just "Tagalog" — Philippine
 *       government's preferred term.</li>
 * </ul>
 */

export interface LanguageEntry {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

export const LANGUAGES: LanguageEntry[] = [
  // Core
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🌐' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🌐' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  // South Asia
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰' },
  { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', flag: '🇳🇵' },
  { code: 'si', name: 'Sinhala', nativeName: 'සිංහල', flag: '🇱🇰' },
  // SE / E Asia
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', name: 'Thai', nativeName: 'ภาษาไทย', flag: '🇹🇭' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'tl', name: 'Filipino (Tagalog)', nativeName: 'Filipino', flag: '🇵🇭' },
  // Europe
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', flag: '🇸🇮' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷' },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски', flag: '🇷🇸' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български', flag: '🇧🇬' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti', flag: '🇪🇪' },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių', flag: '🇱🇹' },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu', flag: '🇱🇻' },
  // Middle East / Caucasus
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'fa', name: 'Persian (Farsi)', nativeName: 'فارسی', flag: '🇮🇷' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱' },
  { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycanca', flag: '🇦🇿' },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪' },
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', flag: '🇦🇲' },
  // Africa
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪' },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹' },
];

const BY_CODE: Record<string, LanguageEntry> = Object.create(null);
for (const l of LANGUAGES) BY_CODE[l.code] = l;

/** Look up a language by code. Returns undefined for unknown codes —
 *  callers should fall back to the code itself or a tenant-supplied
 *  override via {@code i18n.labels}. Strips region suffix as last
 *  resort — {@code "en-GB"} not in registry returns the {@code "en"}
 *  entry. */
export function findLanguage(code: string): LanguageEntry | undefined {
  if (!code) return undefined;
  const direct = BY_CODE[code];
  if (direct) return direct;
  const dash = code.indexOf('-');
  if (dash > 0) return BY_CODE[code.slice(0, dash)];
  return undefined;
}

/** Native-name table {@code LanguageSwitcher} consumes. */
export const NATIVE_NAMES_FROM_REGISTRY: Record<string, string> = (() => {
  const out: Record<string, string> = Object.create(null);
  for (const l of LANGUAGES) out[l.code] = l.nativeName;
  return out;
})();

/** English exonym table for admin labels. */
export const ENGLISH_NAMES_FROM_REGISTRY: Record<string, string> = (() => {
  const out: Record<string, string> = Object.create(null);
  for (const l of LANGUAGES) out[l.code] = l.name;
  return out;
})();
